import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { RelativeTransformData, WhiteboardElement } from 'ng-whiteboard';
import { EMPTY, Observable, Subject, catchError, firstValueFrom, map, takeUntil } from 'rxjs';
import { stringify as uuidStringify } from 'uuid';
import { CanvasData } from '../components/whiteboard/whiteboard.component';
import { Constants } from '../constants';
import { AppHelper } from '../helpers/app.helper';
import { BrowserHelper } from '../helpers/browser.helper';
import { INITIAL_TRANSFORM_DATA } from '../helpers/whiteboard.helper';
import { AgencySettings, initialAgencySettings } from '../models/WebServer/agencySettings';
import { AppModesEnum } from '../models/WebServer/appModesEnum';
import { Message } from '../models/WebServer/message';
import { PartecipantInfo } from '../models/WebServer/partecipantInfo';
import { OPERATOR_ROLES, PartecipantRoles } from '../models/WebServer/partecipantRoles';
import { PartecipantStatus } from '../models/WebServer/partecipantStatus';
import { AppConfig, initialAppConfig } from '../models/appConfig';
import { HubConfig } from '../models/hubConfig';
import { LanguageCode } from '../models/languageCode';
import { DrawingProperties } from '../models/whiteboard';
import { IsOnCallPipe } from '../pipes/isOnCall.pipe';
import { FileCategory, TextMessageIconPipe } from '../pipes/textMessageIcon.pipe';
import { configLoaded } from '../store/actions/app.actions';
import { sessionFailure } from '../store/actions/session.actions';
import { selectAppState } from '../store/selectors/app.selectors';
import { selectPartecipantsStatus } from '../store/selectors/sessionStatus.selectors';
import { AppState } from '../store/states/app.state';
import { I18Service } from './i18.service';


interface WhiteboardConfig {
  editing: boolean;
  hasUnsavedChanges: boolean;
  lastUndoSavedChanges: number; // -1 = sentinel "mai salvato", >= 0 = undoSize al momento del save
  openedFromVideo: boolean;
  sharingEnabled: boolean;
  isReceivingShare: boolean;
  fromWho?: string;
  canvasData?: CanvasData; // used for image type
  elements?: WhiteboardElement[]; // without image
  data: WhiteboardElement[];
  transformData: RelativeTransformData;
  // strokeColor: string;
  // strokeWidth: number;
  // strokeStyle: string;
  // fontFamily: string,
  // fontSize: number
  properties: DrawingProperties;
}


@Injectable({
  providedIn: 'root'
})
export class AppConfigService implements OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private appConfig: AppConfig = initialAppConfig
  private appState$: AppState | undefined;
  private urlParams?: URLSearchParams
  // private sessionStatusState$: SessionStatusState | undefined
  public static readonly ZOOM_LEVELS = [1, 1.5, 2, 3, 4, 6, 8];//, 12, 16, 24, 32, 40, 50, 64];
  public static readonly maxFileSize: number = 30 * 1024 * 1024; // 10MB
  public static supportedFileExtensions: string[] = ['*'];
  private static _brandName: string = initialAppConfig.brandName;
  public static get brandName() { return AppConfigService._brandName; }

  public popupLabelNewDocument: string = '';
  public popupFilename: string = '';
  public popupUrl: string = '';
  public get supportedExtensions() { return AppConfigService.supportedFileExtensions.map(ext => `${ext}`).join(','); }
  public setSupportedExtensions(list: string[]) {
    if (list.includes('*')) { return; } // '*' means all files

    AppConfigService.supportedFileExtensions = list;
  }


  public partecipantsStatus$: PartecipantStatus[] = [];
  // public anyoneOnCall = false;
  public iamOnCall = false;

  static translationLanguages: string[] = [];
  
  public agencySettings: AgencySettings = initialAgencySettings;

  public partecipantId: string = '';
  public isCaller: boolean = false;
  public isReceiver: boolean = false;
  public isGuest: boolean = false;
  public isSuper: boolean = false;
  public isOnSiteOperator: boolean = false;
  public alias: string | null = '';
  public static role: PartecipantRoles = PartecipantRoles.PrimaryClient;
  public static CHATHISTORY_RELOAD = 10; //seconds

  // public realtimeWhiteboardEnabled = false;
  // public whiteboardData?: WhiteboardElement[];
  private _whiteboardBackup?: WhiteboardConfig;

  private _whiteboard: WhiteboardConfig = {
    editing: false,
    hasUnsavedChanges: false,
    lastUndoSavedChanges: -1,
    openedFromVideo: false,
    canvasData: undefined,
    elements: undefined, // tutti gli elementi (tranne l'immagine)
    data: [], //tutti gli elementi
    sharingEnabled: false,
    isReceivingShare: false,
    fromWho: '',
    properties: {
      strokeColor: '#ef5350',
      strokeWidth: 5,
      lineStyle: 'solid',
      fontFamily: 'sans-serif',
      fontSize: 24,
      fontStyle: 'normal',
      fontWeight: 'normal'
    },
    transformData: {selectionBounds: undefined, elementsTranslation: {x:0,y:0}, zoomRelative: 1,x:0,y:0},

  }

  // get whiteboard(): Readonly<WhiteboardConfig> { return this._whiteboard; }

  setWhiteboardCanvasToEdit(canvas?: CanvasData, fromVideo: boolean = false){
    console.log(`setWhiteboardCanvasToEdit fromVideo=${fromVideo}`, canvas)

    if(canvas) {
      //bloccare se c'è già un canvas?? sarebbe strano
      if(this._whiteboard.canvasData) {
        console.warn(`whitbeoard: come mai c'è già un canvasData??`,this._whiteboard.canvasData)
      }
      this._whiteboard.editing = true;
      this._whiteboard.openedFromVideo = fromVideo;
    }
    else {
      this._whiteboard.editing = false;
      this._whiteboard.openedFromVideo = false;
    }
    this._whiteboard.canvasData = canvas;
  }
  setWhiteboardSharedCanvas(canvas?: CanvasData, who?: string) {
    console.log(`setWhiteboardSharedCanvas`, canvas, who)
    if(canvas){

      this._whiteboard.isReceivingShare = true;
    } else {
      //se c'è un backup.. rimettere in edit?
      this._whiteboard.isReceivingShare = false;
    }
    this._whiteboard.canvasData = canvas;
    this._whiteboard.fromWho = who;
  }
  setWhiteboardData(els: WhiteboardElement[]){ console.log(`setWhiteboardData`, els);this._whiteboard.data = els; }
  setWhiteboardElements(els?: WhiteboardElement[]){ console.log(`setWhiteboardElements`, els);this._whiteboard.elements = els; }
  setWhiteboardRelativeTransform(transform: RelativeTransformData){ console.log(`setWhiteboardRelativeTransform`, transform);
    this._whiteboard.transformData = transform; 
  } 
  // setWhiteboardSelectionBounds(bounds?: ViewBoxBounds) { console.log(`setWhiteboardSelectionBounds`, bounds);this._whiteboard.selectionBounds = bounds; }
  // setWhiteboardElementsTranslations(translations?: {x: number, y: number}) { console.log(`setWhiteboardElementsTranslations`, translations);this._whiteboard.elementsTranslations = translations; } 
  setWhiteboardSharing(b: boolean) { console.log(`setWhiteboardSharing`, b);this._whiteboard.sharingEnabled = b; }
  setWhiteboardProperties(props: Partial<DrawingProperties>) { console.log(`setWhiteboardProperties`, props);this._whiteboard.properties = {...this._whiteboard.properties, ...props};}
  setWhiteboardControlTaken(b: boolean, who: string){
     console.log(`[WB-SAVE] setWhiteboardControlTaken b=${b}, who=${who}, myId=${this.partecipantId}, readonly will be=${!(who && who == this.partecipantId && b)}`);
     if(b){this._whiteboard.fromWho = who;}
     if( who && who == this.partecipantId) {
      this._whiteboard.sharingEnabled = b;
      this._whiteboard.editing = b;
     }
    }
  setWhiteboardControlLoose(who: string){ console.log(`[WB-SAVE] setWhiteboardControlLoose who=${who}, lastUndoSavedChanges → -1`);this._whiteboard.isReceivingShare = true;this._whiteboard.sharingEnabled = false; this._whiteboard.editing = false; this._whiteboard.fromWho = who;this._whiteboard.lastUndoSavedChanges = -1;}
  setWhiteboardHasUnsavedChanges(hasChanges: boolean){ console.log(`[WB-SAVE] setWhiteboardHasUnsavedChanges(${hasChanges})`);this._whiteboard.hasUnsavedChanges = hasChanges; }
  markWhiteboardAsSaved(undoSize: number){ console.log(`[WB-SAVE] markWhiteboardAsSaved(undoSize=${undoSize}) → hasUnsavedChanges=false`);this._whiteboard.hasUnsavedChanges = false; this._whiteboard.lastUndoSavedChanges = undoSize;}


  whiteboardBackup(){
    if(this._whiteboard.editing) {
      this._whiteboardBackup = AppHelper.deepCopy(this._whiteboard);
      console.log(`whiteboardBackup`, this._whiteboardBackup);
      
      this.whiteboardReset();
    }
  }
  whiteboardReset(){
      console.log(`whiteboardReset`)
      this._whiteboard.editing = false;
      this._whiteboard.sharingEnabled = false;
      this._whiteboard.isReceivingShare = false;
      this._whiteboard.hasUnsavedChanges = false;
      this._whiteboard.lastUndoSavedChanges = -1;
      this._whiteboard.openedFromVideo = false;
      this.setWhiteboardSharedCanvas(undefined)
      this.setWhiteboardCanvasToEdit(undefined);
      this.setWhiteboardElements(undefined);
      this.setWhiteboardData([]);
      this.setWhiteboardRelativeTransform(INITIAL_TRANSFORM_DATA);
  }

  //whiteboard getters
  getWhiteboardBackupCanvas(): CanvasData | undefined {

    this._whiteboard.editing = false;
    this._whiteboard.isReceivingShare = false;
    this._whiteboard.sharingEnabled = false;
    this._whiteboard.hasUnsavedChanges = false;

    if(this._whiteboardBackup){
      console.log(`getWhiteboardBackupCanvas`, this._whiteboardBackup);
      this._whiteboard.canvasData = this._whiteboardBackup.canvasData;
      this._whiteboard.elements = this._whiteboardBackup.elements;
      this._whiteboard.transformData = this._whiteboardBackup.transformData;
      this._whiteboard.hasUnsavedChanges = this._whiteboardBackup.hasUnsavedChanges;
      this._whiteboard.lastUndoSavedChanges = this._whiteboardBackup.lastUndoSavedChanges;
      this._whiteboard.openedFromVideo = this._whiteboardBackup.openedFromVideo;
      this._whiteboard.editing = true;
      this._whiteboardBackup = undefined;
    }

    return this._whiteboard.canvasData;
  }
  get whiteboardReadonly(): boolean {
    const r= AppConfigService.role == PartecipantRoles.SecondaryClient || !this._whiteboard.editing; //si potrebbe prendere da AS
    return r;
  }
  get whiteboardHasUnsavedChanges(): boolean { return this._whiteboard.hasUnsavedChanges; }
  get whiteboardSavedUndoSize(): number { return this._whiteboard.lastUndoSavedChanges; }
  get whiteboardOpenedFromVideo(): boolean { return this._whiteboard.openedFromVideo; }
  get whiteboardSharingEnabled(): boolean { return this._whiteboard.sharingEnabled; }
  get whiteboardFromWho(): string { return this._whiteboard.fromWho || ''; }
  get whiteboardCanvasData(): CanvasData | undefined { return this._whiteboard.canvasData; }
  get whiteboardElements(): WhiteboardElement[] | undefined { return this._whiteboard.elements; }
  get whiteboardData(): WhiteboardElement[] { return this._whiteboard.data; }
  get whiteboardTransformData(): RelativeTransformData { return this._whiteboard.transformData; }
  get whiteboardProperties(): DrawingProperties { return this._whiteboard.properties; }
  get whiteboardIsEditing(): boolean { return this._whiteboard.editing; }
  get whiteboardIsReceivingShare(): boolean { return this._whiteboard.isReceivingShare; }


  public isMobilePhone = BrowserHelper.isMobilePhone();

  private origLogDeug: { frontEndLogEnabled: boolean, frontEndLogLevel: string } = {
    frontEndLogEnabled: initialAppConfig.frontEndLogEnabled,
    frontEndLogLevel: initialAppConfig.frontEndLogLevel
  }
  private static _debugEnabled = false;
  static get debugEnabled() { return AppConfigService._debugEnabled; }
  public inDebug() { return AppConfigService._debugEnabled; }


  minGuestWidth: number = 700;
  actualWidth: number = 700;
  private static _textMessageIcon: TextMessageIconPipe;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private i18Service: I18Service,
    public onCallPipe: IsOnCallPipe,
    private textMessageIcon: TextMessageIconPipe
    //  private sessionService: SessionService
  ) {
    AppConfigService._textMessageIcon = textMessageIcon;
    console.debug(`AppConfigService constructor`)
    this.minGuestWidth = parseInt(getComputedStyle(document.documentElement)
      .getPropertyValue('--guest-max-width').replace('px', '')); // 710px

    window.onresize = () => this.actualWidth = window.innerWidth;
    this.actualWidth = window.innerWidth;

    this.store.select(selectAppState).pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.appState$ = data
      if (data) { this.agencySettings = data.session.config.agencySettings; }
    });

    // this.store.select(selectSessionStatus).pipe(
    //   takeUntil(this.destroy$)
    // ).subscribe(data => this.sessionStatusState$ = data);
    this.store.select(selectPartecipantsStatus).pipe(
      takeUntil(this.destroy$)
    ).subscribe(data => {
      this.partecipantsStatus$ = data
      // this.anyoneOnCall = data.some(z => [PhoneStatusEnum.CALLING, PhoneStatusEnum.RINGING, PhoneStatusEnum.REMOTE_RINGING, PhoneStatusEnum.TALKING].includes(z.phoneStatus))
      const myStatus = data.find(z => z.partecipantId == this.partecipantId);
      if (myStatus) this.iamOnCall = this.onCallPipe.transform(myStatus.phoneStatus, true);
    });

  }
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }


  load(): any {
    // [PERF-LOAD-02] Config Load Start
    const perfNow = performance.now();
    const elapsed = perfNow - ((window as any).perfMarks?.browserStart || 0);
    console.log(`[PERF-LOAD-02] Config Load Start | Time: ${perfNow.toFixed(2)}ms | Elapsed: ${elapsed.toFixed(2)}ms`);

    const jsonFile = `assets/config.json?v=${AppHelper.getBuild()}`;

    const p = new Promise((res, rej) => {
      this.http.get(jsonFile).subscribe(
        (data: any) => {
          // [PERF-LOAD-03] Config Loaded
          const perfNow2 = performance.now();
          const elapsed2 = perfNow2 - ((window as any).perfMarks?.browserStart || 0);
          console.log(`[PERF-LOAD-03] Config Loaded | Time: ${perfNow2.toFixed(2)}ms | Elapsed: ${elapsed2.toFixed(2)}ms`);

          this.setConfig(data).then(_ => res(true));

        }),
        catchError((err: any) => {
          console.error(`Error loading config file: ${jsonFile}`, err);
          return EMPTY
        })
    });

    p.then(z => {
      // console.debug(`*********????????????????`,z);
    })

    this.loadLanguageCodes();


    return p;

  }

  private loadLanguageCodes() {
    const selfThis = this;
    const jsonFile = `assets/i18n/language_codes.json?v=${AppHelper.getBuild()}`;
    this.http.get(jsonFile).subscribe(
      (data: any) => {

        I18Service.setLanguageCodes(data as LanguageCode[]);
        console.log(`********* loaded ${data.length} languages codes`);

      }),
      catchError((err: any) => {
        console.error(`Error loading config file: ${jsonFile}`, err);
        return EMPTY
      })

  }

  private async setConfig(data: AppConfig): Promise<any> {
    return new Promise((res, rej) => {

      const appConfig = { ...initialAppConfig, ...data, rtrProxy: { ...initialAppConfig.rtrProxy, ...data.rtrProxy } };

      this.origLogDeug = {
        frontEndLogEnabled: appConfig.frontEndLogEnabled,
        frontEndLogLevel: appConfig.frontEndLogLevel
      }


      const queryString = window.location.search;
      this.urlParams = new URLSearchParams(queryString);
      const urlParams = this.urlParams;
      // console.log(`AppConfig loaded`, urlParams.get('c'));

      this.partecipantId = urlParams.get('caller') || urlParams.get('receiver') || urlParams.get('guest') || urlParams.get('super') || '';
      if (!this.partecipantId) { // compact
        const compactGuid = urlParams.get('c') || urlParams.get('r') || urlParams.get('g') || urlParams.get('s') || urlParams.get('o')
        if (compactGuid) {
          this.partecipantId = decodeCompactGuid(compactGuid);
        }
      }
      this.partecipantId = this.partecipantId?.toLowerCase();
      // if (!partecipantId) { // strange.., so.. ERROR
      //     errorInfo = new ErrorInfo();
      //     errorInfo.message = I18Service.get('POPUP.SESSIONNOTFOUNDBODY');
      //     errorInfo.showReloadButton = false;
      // }
      this.isCaller = !!urlParams.get('c') || !!urlParams.get('caller') || false;
      this.isReceiver = !!urlParams.get('r') || !!urlParams.get('receiver') || false;
      this.isGuest = !!urlParams.get('g') || !!urlParams.get('guest') || false;
      this.isSuper = !!urlParams.get('s') || !!urlParams.get('super') || false;
      this.isOnSiteOperator = !!urlParams.get('o') || false;



      const role: PartecipantRoles =
        (this.isReceiver) ? PartecipantRoles.PrimaryClient
          : (this.isCaller) ? PartecipantRoles.SecondaryClient
            : (this.isGuest) ? PartecipantRoles.GuestClient
              : (this.isSuper) ? PartecipantRoles.SuperClient
                : PartecipantRoles.OnSiteOperator;

      if (this.isSuper) this.isReceiver = true;
      if (this.isOnSiteOperator) {
        this.isGuest = true;
        // if (!this.alias) this.alias='pippo';
      }

      console.log(`config loaded => partecipantId: ${this.partecipantId} - role: ${role}`);
      AppConfigService.role = role;
      AppConfigService._brandName = appConfig.brandName || initialAppConfig.brandName;

      // if (this.isReceiver) { // scambia le url per il server sip da contattare
      //   appConfig.rtrProxy.wssUrl = appConfig.rtrProxy.primaryClient.wssUrl;
      //   appConfig.rtrProxy.testUrl = appConfig.rtrProxy.primaryClient.testUrl;
      // }

      this.appConfig = { ...appConfig };

      AppConfigService.translationLanguages = this.appConfig.translationLanguages;
        const initialLanguage = urlParams.get(Constants.PARAM_LANG);
      const guiLanguage = initialLanguage || I18Service.language || appConfig.defaultLang;

      this.i18Service.setLanguage(guiLanguage).then(async (lang) => {
        console.log(`first translation loaded '${lang}'`)
 
        const storageKey=`alias-${this.partecipantId}`;
        this.alias = urlParams.get(Constants.PARAM_ALIAS) || localStorage.getItem(storageKey) || '';


        if (role == PartecipantRoles.OnSiteOperator) {
          try {
            const newOnSiteOp: PartecipantInfo = await firstValueFrom(this.getRequest<PartecipantInfo>(`${this.apiBaseUrl}/session/addOnSiteParticipant/${this.partecipantId}/${initialLanguage || lang}/${this.alias}`));

            localStorage.setItem(storageKey, newOnSiteOp.alias)

            this.partecipantId = newOnSiteOp.partecipantId;
            this.alias = newOnSiteOp.alias;
            console.log(`config loaded => new OnSitePartecipantId: ${this.partecipantId} - role: ${role} alias: ${this.alias}`);
            if (!urlParams.get(Constants.PARAM_ALIAS)) {
              const newurl = new URL(window.location.href);
              newurl.searchParams.set(Constants.PARAM_ALIAS, this.alias);
              window.location.href = newurl.toString();
              rej();
              return;
            }
          } catch (error) {
            // this.store.dispatch(loadingMessage({ message: '' }));
            this.store.dispatch(sessionFailure({ error}));
            res(false);
            return;
          }
        }


        if (initialLanguage !== null) {
          this.i18Service.saveLanguage(initialLanguage).then((l: string) => {
            this.store.dispatch(configLoaded({ config: appConfig, partecipant: { id: this.partecipantId, role } }))
            // [PERF-LOAD-04] Config Dispatched
            const perfNow3 = performance.now();
            const elapsed3 = perfNow3 - ((window as any).perfMarks?.browserStart || 0);
            console.log(`[PERF-LOAD-04] Config Dispatched | Time: ${perfNow3.toFixed(2)}ms | Elapsed: ${elapsed3.toFixed(2)}ms`);
            res(true);
          });
          return;
        }
        else {
          this.store.dispatch(configLoaded({ config: appConfig, partecipant: { id: this.partecipantId, role } }))
          // [PERF-LOAD-04] Config Dispatched
          const perfNow3 = performance.now();
          const elapsed3 = perfNow3 - ((window as any).perfMarks?.browserStart || 0);
          console.log(`[PERF-LOAD-04] Config Dispatched | Time: ${perfNow3.toFixed(2)}ms | Elapsed: ${elapsed3.toFixed(2)}ms`);
          res(true)
        }
      })


    })
  }

  private getRequest<T>(url: string): Observable<T> {

    return this.http.get<T>(url, { headers: { 'SM-PartecipantId': this.partecipantId } }).pipe(
      map((response: T) => {
        return response
      })
      , catchError(err => { throw err; })
    );

  }



  //use getter only on .ts not in html !!
  get extendSessionMinutes() { return this.appConfig.extendSessionMinutes; }
  get defaultLang() { return this.agencySettings?.defaultLanguage || this.appConfig.defaultLang; }

  get localDev() { return this.getValue('localDev'); }
  get apiBaseUrl() { return this.getValue('apiBaseUrl'); }
  get language() { return this.i18Service.getLanguage(); }

  get AS() { return this.appState$; } // rimuovere?? torna comodo in alcuni contesti e funzionano anche gli "aggiornamenti"
  get partecipantIds() { return this.appState$?.session.parameters.partecipantIds || []; }
  get sessionId() { return this.appState$?.session.sessionId; }

  get rtrProxy() { return this.appConfig.rtrProxy; }
  get frontEndLogLevel() { return this.appConfig.frontEndLogLevel; }
  get frontEndLogEnabled() { return this.appConfig.frontEndLogEnabled; }
  // get browserCompatibility() {return this.appState?.browserCompatibility; } //TODO: //dave da spostare su partecipantStatus
  get deviceControls() { return this.appConfig.deviceControls; }
  get operatorDeviceControls() { return this.appConfig.operatorDeviceControls; }
  get desiredImageSize() { return this.appConfig.desiredImageSize; }
  get geoPermissionTimeout() { return this.appConfig.geoPermissionTimeout; }
  get saveBusinessEvent() { return this.appConfig.saveBusinessEvent; }
  get operatorButtons() { return this.appConfig.operatorButtons; }
  get includeImagesInChat() { return this.appConfig.includeImagesInChat; }
  get minutesExtension() { return this.appConfig.minutesExtension; }
  get maxImageDimensions() { return this.appConfig.maxImageDimensions; }

  // get countriesCodes() { return this.appConfig.countries.map(z => typeof z === 'string' ? z : z.flag)}
  get signalRHub(): HubConfig {
    return {
      hubName: '',
      automaticReconnect: true,
      url: `${this.appConfig.signalRUlr}?SM-PartecipantId=${this.partecipantId}`,
      options: { headers: { 'SM-PartecipantId': this.partecipantId } }
    };
  }
  get myPartecipantStatus() { return this && this.partecipantsStatus$?.find(p => p.partecipantId == this.partecipantId); }
  get operatorsStatus() { return this && this.otherPartecipantsStatus.filter(z => OPERATOR_ROLES.includes(z.partecipantRole)) || []; }
  get otherPartecipantsStatus() { return this && this.partecipantsStatus$?.filter(p => p.partecipantId != this.partecipantId) || []; }
  get otherPartecipantsOnCall() {
    return this.otherPartecipantsStatus
      .filter(z => !OPERATOR_ROLES.includes(z.partecipantRole)) // not operator
      .filter(z => this.onCallPipe.transform(z.phoneStatus)) // and already in call
  }
  get anyOtherPaticipantOnVideoSection() {
    return this.otherPartecipantsStatus
      .filter(z => !OPERATOR_ROLES.includes(z.partecipantRole)) // not operator
      .filter(z => !this.onCallPipe.transform(z.phoneStatus)) // not already in call
      .filter(z => z.appModes?.includes(AppModesEnum.Video)); // but on video section
  }


  get sessionConfig() { return this && this.appState$ && this.appState$?.session.config; }

  get showingVideo() { return this && this.appState$ && this.appState$.appModes.includes(AppModesEnum.Video); }

  getTargetPresence() { //TODO perde di significato quando multi

    if (!this || !this.partecipantsStatus$) {
      return undefined;
    }

    return (this.appState$?.isCaller)
      ? this.partecipantsStatus$?.some(z => z.partecipantRole == PartecipantRoles.PrimaryClient)
      : this.partecipantsStatus$?.some(z => z.partecipantRole == PartecipantRoles.PrimaryClient)
  }

  getPartecipantsStatus() { return this && this.partecipantsStatus$ || []; }

  public logDebug(enable: boolean) {
    console.log(`remote log enabled: ${enable}`);
    if (enable) {
      this.appConfig.frontEndLogEnabled = true;
      this.appConfig.frontEndLogLevel = 'Error,Warning,Info,Debug';
      AppConfigService._debugEnabled = true;
    } else {
      this.appConfig.frontEndLogEnabled = this.origLogDeug.frontEndLogEnabled;
      this.appConfig.frontEndLogLevel = this.origLogDeug.frontEndLogLevel;
      AppConfigService._debugEnabled = false;
    }
  }

  public getUrlParam(k: string): string | null | undefined {
    return this.urlParams?.get(k);
  }

  private getValue(p: string): any {
    if (!this.appConfig) {
      throw Error('Config file not loaded!');
    }

    return this.appConfig[p as keyof AppConfig];

  }


  isImage(message: Message): boolean {
    return AppConfigService.isImage(message);
  }
  
  static isImage(message: Message): boolean {
    return [FileCategory.Image/*, FileCategory.Video, FileCategory.Audio*/].includes( this._textMessageIcon.transform(message).type)
  }

}





function decodeCompactGuid(compactGuid: string): string {

  function replaceAll(original: string, find: string, replacement: string): string {
    const pieces = original.split(find);
    return pieces.join(replacement);
  }

  let str = replaceAll(compactGuid, ".", "/")
  str = replaceAll(str, "_", "/");
  str = replaceAll(str, "-", "+");
  str = `${str}==`;
  // `${compactGuid.replace("_", "/").replace("-", "+")}==`;
  let data = '';
  try { data = window.atob(str); } catch (e) {
    console.error(e);
    return '';
  }
  const array = Uint8Array.from(data, b => b.charCodeAt(0));
  let orderArray: number[] = [];
  if (array.length == 16) {
    orderArray = [
      array[3], array[2], array[1], array[0],
      array[5], array[4],
      array[7], array[6],
      array[8], array[9],
      array[10], array[11], array[12], array[13], array[14], array[15]
    ]
  }
  var decodedGuid = uuidStringify(orderArray);
  let guid = decodedGuid.toString();
  return guid;
}